import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { PageContainer } from '@/customer/components/page-container'
import {
  type SubscriptionRequirement,
  usePetSubscriptionGuard,
} from '@/customer/hooks/use-pet-subscription-guard'

/**
 * Subscription-status guard for pet-scoped pages. Must be nested
 * inside `<PetScope />`.
 *
 * `require` selects the named guards that route based on the pet's
 * Stripe subscription status. The guard uses OR semantics - children
 * render if any requirement is satisfied. When none are satisfied,
 * the first guard's redirect is used.
 */
export function PetSubscriptionGuard({
  require,
  poll,
  loadingMessage,
  children,
}: {
  require: SubscriptionRequirement[]
  poll?: boolean
  loadingMessage?: string
  children: ReactNode
}) {
  const { isPending, redirect } = usePetSubscriptionGuard(require, { poll })

  if (isPending) {
    return (
      <PageContainer className="items-center justify-center gap-6">
        {loadingMessage ? (
          <p className="text-body-md text-muted-foreground">{loadingMessage}</p>
        ) : null}
        <LoadingSpinner />
      </PageContainer>
    )
  }

  if (redirect) return <Navigate to={redirect} replace />

  return children
}
